/**
 * Super Admin Hooks
 * Hooks pour gérer les fonctionnalités super admin
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { API_ENDPOINTS } from '@/lib/config';

interface App {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  domain: string;
  logoUrl: string | null;
  color: string | null;
  organizationIds: string[];
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hook pour récupérer toutes les apps (super admin)
 */
export const useSuperAdminApps = () => {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['super-admin', 'apps'],
    queryFn: async (): Promise<{ apps: App[] }> => {
      const token = await getToken();

      const response = await fetch(`${API_ENDPOINTS.superAdmin}/apps`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch apps');
      }

      return response.json();
    },
    enabled: isSignedIn,
  });
};

/**
 * Hook pour mettre à jour les organizations d'une app
 */
export const useUpdateAppOrganizations = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      appId,
      organizationIds,
    }: {
      appId: string;
      organizationIds: string[];
    }) => {
      const token = await getToken();

      const response = await fetch(`${API_ENDPOINTS.superAdmin}/apps/${appId}/organizations`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organizationIds }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update app organizations');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalider le cache pour recharger les apps
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'apps'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'apps'] });
    },
  });
};

/**
 * Hook pour ajouter une organization à une app
 */
export const useAddOrganizationToApp = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appId, organizationId }: { appId: string; organizationId: string }) => {
      const token = await getToken();

      const response = await fetch(`${API_ENDPOINTS.superAdmin}/apps/${appId}/organizations/add`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organizationId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add organization');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'apps'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'apps'] });
    },
  });
};

/**
 * Hook pour retirer une organization d'une app
 */
export const useRemoveOrganizationFromApp = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appId, organizationId }: { appId: string; organizationId: string }) => {
      const token = await getToken();

      const response = await fetch(
        `${API_ENDPOINTS.superAdmin}/apps/${appId}/organizations/remove`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ organizationId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove organization');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'apps'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'apps'] });
    },
  });
};

export interface Organization {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
  membersCount: number;
  createdAt: number;
}

/**
 * Hook pour récupérer la liste des organizations depuis Clerk
 */
export const useSuperAdminOrganizations = () => {
  const { getToken, isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['super-admin', 'organizations'],
    queryFn: async (): Promise<{ organizations: Organization[]; total: number }> => {
      const token = await getToken();

      const response = await fetch(`${API_ENDPOINTS.superAdmin}/organizations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      return response.json();
    },
    enabled: isSignedIn,
  });
};
